import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { ReadonlyFieldState, form, minLength, required } from '@angular/forms/signals';
import { fieldValidationErrors } from '@core/misc/signal-forms.util';
import { authLogIn, authResetLoadState, authResetSessionExpiredState } from '@core/modules/auth/state/auth.actions';
import {
  AUTH_LOG_IN_MISSING_FIELDS_MESSAGE,
  AUTH_SESSION_EXPIRED_VISIBILITY_SECONDS,
  NGXS_AUTH_FORM
} from '@core/modules/auth/state/auth.const';
import { AuthState } from '@core/modules/auth/state/auth.state';
import { SVGIcon } from '@data/svg-icons.data';
import { environment } from '@env/environment';
import { UpdateFormValue } from '@ngxs/form-plugin';
import { dispatch, select } from '@ngxs/store';
import { differenceInMilliseconds, differenceInSeconds } from 'date-fns';

/** Minimum number of characters accepted for both credentials. */
const CREDENTIAL_MIN_LENGTH = 3;

/** Value model backing the log-in signal form. */
interface LogInFormModel {
  login: string;
  password: string;
}

@Injectable()
export class LogInPageService {
  /** Hides the "session expired" notice once its window is over; cleared with the service. */
  #sessionExpiredTimer: ReturnType<typeof setTimeout> | undefined;

  constructor() {
    inject(DestroyRef).onDestroy(() => clearTimeout(this.#sessionExpiredTimer));
  }

  // #region Store bindings

  readonly #sessionExpiredAt = select(AuthState.sessionExpiredAt);
  readonly #resetLoadState = dispatch(authResetLoadState);
  readonly #resetSessionExpiredState = dispatch(authResetSessionExpiredState);
  readonly #logIn = dispatch(authLogIn);
  readonly #updateFormValue = dispatch(UpdateFormValue);

  /** Load status of the log-in request; owned by `AuthState`. */
  readonly formLoadState = select(AuthState.logInFormStates);

  // #endregion

  // #region Form

  readonly #credentials = signal<LogInFormModel>({ login: '', password: '' });

  /**
   * Log-in form. Submission is declared here so the `[formRoot]` directive can drive it: it marks the
   * fields as touched, blocks invalid submissions and only then runs the action.
   */
  readonly logInForm = form(
    this.#credentials,
    (path) => {
      required(path.login);
      minLength(path.login, CREDENTIAL_MIN_LENGTH);
      required(path.password);
      minLength(path.password, CREDENTIAL_MIN_LENGTH);
    },
    {
      submission: {
        action: async () => this.#dispatchLogIn(),
        // Invalid attempts never reach the action, so the flag that reveals the validation
        // messages has to be raised here too.
        onInvalid: () => this.submitAttempted.set(true)
      }
    }
  );

  /** `true` once the user has pressed "Log In"; gates the inline validation messages. */
  readonly submitAttempted = signal(false);

  // #endregion

  // #region View state

  /** External links rendered below the form. */
  readonly loginPageLinks = environment.loginPageLinks;

  /** `true` when the user was sent back here because the previous session expired. */
  readonly isSessionExpired = signal(false);

  /** `true` while the password is revealed through the trailing eye icon. */
  readonly passwordVisible = signal(false);

  /** Validation errors under the username input, in the shape `KzInputDirective` renders. */
  readonly loginErrors = computed(() => this.#visibleErrors(this.logInForm.login()));

  /** Validation errors under the password input. */
  readonly passwordErrors = computed(() => this.#visibleErrors(this.logInForm.password()));

  /** `type` attribute of the password input. */
  readonly passwordInputType = computed(() => (this.passwordVisible() ? 'text' : 'password'));

  /** Trailing icon of the password input. */
  readonly passwordIcon = computed<SVGIcon>(() => (this.passwordVisible() ? 'eye_slash' : 'eye'));

  /** Accessible name of the eye icon's button (translation key). */
  readonly passwordToggleLabel = computed(() => (this.passwordVisible() ? 'Hide password' : 'Show password'));

  /** `true` while the log-in request is in flight. */
  readonly isSubmitting = computed(() => this.formLoadState().status === 'loading');

  /** `true` when the submit button must stay disabled. */
  readonly isSubmitDisabled = computed(() => this.submitAttempted() && this.logInForm().invalid());

  /** `true` when the form-level error line below the inputs is rendered. */
  readonly hasFormError = computed(
    () => this.submitAttempted() && (this.logInForm().invalid() || this.formLoadState().status === 'error')
  );

  /** Form-level error text, falling back to the generic "fill all fields" copy. */
  readonly formErrorMessage = computed(() => {
    const { errorMessage } = this.formLoadState();
    return errorMessage?.length ? errorMessage : AUTH_LOG_IN_MISSING_FIELDS_MESSAGE;
  });

  // #endregion

  /**
   * Clears the previous load state and surfaces the "session expired" notice when it is still recent.
   * The notice stays for the rest of its window (R-SAL16: 20 seconds after the expiry) and then hides, also on a page that stays open.
   */
  init(): void {
    this.#resetLoadState();

    const sessionExpiredAt = this.#sessionExpiredAt();
    if (!sessionExpiredAt) return;

    const now = new Date();
    if (differenceInSeconds(now, sessionExpiredAt) < AUTH_SESSION_EXPIRED_VISIBILITY_SECONDS) {
      const remainingMs =
        AUTH_SESSION_EXPIRED_VISIBILITY_SECONDS * 1000 - differenceInMilliseconds(now, sessionExpiredAt);
      this.isSessionExpired.set(true);
      this.#resetSessionExpiredState();
      clearTimeout(this.#sessionExpiredTimer);
      this.#sessionExpiredTimer = setTimeout(() => this.isSessionExpired.set(false), Math.max(remainingMs, 0));
    }
  }

  /** Reveals or masks the password from the trailing eye icon. */
  togglePasswordVisibility(): void {
    this.passwordVisible.update((visible) => !visible);
  }

  /**
   * Mirrors the credentials into `AuthState` and fires the log-in action.
   *
   * The dispatch is deliberately not awaited: `AuthState` owns the submission lifecycle — it publishes
   * loading/error status through `formLoadState` and lets failures reach the global NGXS error handler.
   * Awaiting it here would swallow those failures instead.
   */
  #dispatchLogIn(): void {
    this.submitAttempted.set(true);
    this.#updateFormValue({ path: NGXS_AUTH_FORM, value: this.#credentials() });
    this.#logIn();
  }

  /** Field errors, hidden until the user has pressed "Log In" at least once. */
  #visibleErrors(state: ReadonlyFieldState<unknown>): ValidationErrors | null {
    return this.submitAttempted() ? fieldValidationErrors(state) : null;
  }
}
